// SHADOW MARKET™ — Kids 5 mini-games (compact additive module).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Personaje } from "./Personaje";
import { getKidsUi } from "../../i18n/kids-academy";

// ─── Game 1 · AtrapaMonedas ────────────────────────────────────────────
const AtrapaMonedas = ({ lang, onComplete }) => {
  const ui = getKidsUi(lang);
  const [coins, setCoins] = useState([]);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(30);
  const [running, setRunning] = useState(false);
  const counter = useRef(0);
  const rafRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!running) return undefined;
    let last = Date.now();
    let spawn = 0;
    const loop = () => {
      const now = Date.now();
      const dt = (now - last) / 1000;
      last = now;
      spawn += dt;
      if (spawn > 0.65) {
        setCoins((cs) => [
          ...cs,
          {
            id: counter.current++,
            x: 5 + Math.random() * 88,
            y: -8,
            type: Math.random() > 0.3 ? "btc" : "fiat",
            v: 0.6 + Math.random() * 0.9,
          },
        ]);
        spawn = 0;
      }
      setCoins((cs) => cs.map((c) => ({ ...c, y: c.y + c.v })).filter((c) => c.y < 105));
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    timerRef.current = setInterval(() => {
      setTime((t) => {
        if (t <= 1) {
          setRunning(false);
          clearInterval(timerRef.current);
          cancelAnimationFrame(rafRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearInterval(timerRef.current);
    };
  }, [running]);

  useEffect(() => {
    if (!running && time === 0) onComplete && onComplete(score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, time]);

  const tap = (id, type) => {
    setCoins((cs) => cs.filter((c) => c.id !== id));
    setScore((s) => (type === "btc" ? s + 10 : Math.max(0, s - 5)));
  };

  return (
    <div data-testid="kids-game-atrapa">
      <h3 className="mb-2 text-center text-lg font-black text-white">¡Atrapa los ₿!</h3>
      <div className="mb-3 flex justify-between text-sm text-white/70">
        <span>⏱ {time}s</span>
        <span>⭐ {score} {ui.points}</span>
      </div>
      <div className="relative h-[280px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black">
        {coins.map((c) => (
          <button
            key={c.id}
            onClick={() => running && tap(c.id, c.type)}
            className="absolute flex items-center justify-center rounded-full font-black"
            style={{
              left: `${c.x}%`,
              top: `${c.y}%`,
              width: 44,
              height: 44,
              transform: "translateX(-50%)",
              background: c.type === "btc" ? "#f5a623" : "#475569",
              border: c.type === "btc" ? "2px solid #c4921a" : "2px solid #64748b",
              color: c.type === "btc" ? "#0a0b16" : "#cbd5e1",
              fontSize: 20,
              cursor: "pointer",
              boxShadow: c.type === "btc" ? "0 0 14px rgba(245,166,35,.5)" : "none",
            }}
          >
            {c.type === "btc" ? "₿" : "$"}
          </button>
        ))}
        {!running && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur">
            {time === 0 ? (
              <>
                <div className="text-2xl font-black text-orange-300">🎉 {score} {ui.points}</div>
                <button
                  onClick={() => { setScore(0); setTime(30); setCoins([]); setRunning(true); }}
                  className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2 text-xs font-black uppercase tracking-widest text-black"
                >
                  {ui.game_again}
                </button>
              </>
            ) : (
              <button
                onClick={() => setRunning(true)}
                className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-3 text-sm font-black uppercase tracking-widest text-black shadow-[0_0_25px_rgba(245,166,35,.4)]"
              >
                {ui.play}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Game 2 · QuizSatoshi ──────────────────────────────────────────────
const QUIZ_QS = {
  es: [
    { q: "¿En qué año nació Bitcoin?", a: ["2009", "1999", "2020"], correct: 0 },
    { q: "¿Cuántos bitcoins habrá en total?", a: ["21 millones", "Infinitos", "1 millón"], correct: 0 },
    { q: "¿Qué significa HODL?", a: ["Vender rápido", "Aguantar fuerte", "Comer pizza"], correct: 1 },
    { q: "¿Quién controla Bitcoin?", a: ["Un banco", "El gobierno", "Nadie y todos"], correct: 2 },
  ],
  en: [
    { q: "What year was Bitcoin born?", a: ["2009", "1999", "2020"], correct: 0 },
    { q: "How many bitcoins will there be in total?", a: ["21 million", "Infinite", "1 million"], correct: 0 },
    { q: "What does HODL mean?", a: ["Sell fast", "Hold tight", "Eat pizza"], correct: 1 },
    { q: "Who controls Bitcoin?", a: ["A bank", "The government", "No one and everyone"], correct: 2 },
  ],
  sk: [
    { q: "V ktorom roku sa zrodil Bitcoin?", a: ["2009", "1999", "2020"], correct: 0 },
    { q: "Koľko bitcoinov bude celkovo?", a: ["21 miliónov", "Nekonečno", "1 milión"], correct: 0 },
    { q: "Čo znamená HODL?", a: ["Rýchlo predať", "Pevne držať", "Jesť pizzu"], correct: 1 },
    { q: "Kto kontroluje Bitcoin?", a: ["Banka", "Vláda", "Nikto a všetci"], correct: 2 },
  ], de: [{ q: "In welchem Jahr wurde Bitcoin geboren?", a: ["2009", "1999", "2020"], correct: 0 }, { q: "Wie viele Bitcoins wird es insgesamt geben?", a: ["21 Millionen", "Unendlich", "1 Million"], correct: 0 }, { q: "Was bedeutet HODL?", a: ["Schnell verkaufen", "Stark festhalten", "Pizza essen"], correct: 1 }, { q: "Wer kontrolliert Bitcoin?", a: ["Eine Bank", "Die Regierung", "Niemand und alle"], correct: 2 }], "zh-CN": [{ q: "比特币诞生于哪一年？", a: ["2009", "1999", "2020"], correct: 0 }, { q: "比特币的总供应量是多少？", a: ["2100万", "无限", "100万"], correct: 0 }, { q: "HODL是什么意思？", a: ["快速卖出", "坚定持有", "吃披萨"], correct: 1 }, { q: "谁控制着比特币？", a: ["银行", "政府", "没有人也是所有人"], correct: 2 }],
};

const QuizSatoshi = ({ lang, onComplete }) => {
  const ui = getKidsUi(lang);
  const qs = QUIZ_QS[lang] || QUIZ_QS.en || QUIZ_QS.es;
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null); // 'ok' | 'no' | null
  const [done, setDone] = useState(false);
  const q = qs[idx];

  const pick = (i) => {
    if (feedback) return;
    const ok = i === q.correct;
    setFeedback(ok ? "ok" : "no");
    if (ok) setScore((s) => s + 25);
    setTimeout(() => {
      setFeedback(null);
      if (idx + 1 < qs.length) {
        setIdx((n) => n + 1);
      } else {
        setDone(true);
        setTimeout(() => onComplete && onComplete(ok ? score + 25 : score), 800);
      }
    }, 1100);
  };

  return (
    <div data-testid="kids-game-quiz">
      <div className="mb-4 flex items-center gap-4">
        <Personaje
          id="satoshi"
          estado={feedback === "ok" ? "feliz" : feedback === "no" ? "triste" : "idle"}
          size={80}
        />
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-orange-300">
            {ui.mission} {idx + 1} / {qs.length}
          </div>
          <div className="text-base font-bold text-white">{q.q}</div>
        </div>
        <div className="rounded-full border border-orange-400/30 bg-orange-400/10 px-3 py-1 font-mono text-xs font-bold text-orange-300">
          {score}
        </div>
      </div>
      <div className="grid gap-2">
        {q.a.map((a, i) => (
          <button
            key={i}
            onClick={() => pick(i)}
            disabled={!!feedback || done}
            data-testid={`kids-quiz-opt-${i}`}
            className={`rounded-xl border px-4 py-3 text-left text-sm font-bold transition-colors ${
              feedback && i === q.correct
                ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-200"
                : feedback === "no" && i !== q.correct
                ? "border-red-400/30 bg-red-400/10 text-red-200/80"
                : "border-white/10 bg-white/[0.04] text-white/80 hover:border-orange-400/40"
            }`}
          >
            {a}
          </button>
        ))}
      </div>
      {feedback && (
        <div className={`mt-3 text-center text-sm font-bold ${feedback === "ok" ? "text-emerald-300" : "text-red-300"}`}>
          {feedback === "ok" ? ui.correct : ui.incorrect}
        </div>
      )}
    </div>
  );
};

// ─── Game 3 · HodlChallenge ────────────────────────────────────────────
const HodlChallenge = ({ lang, onComplete }) => {
  const ui = getKidsUi(lang);
  const [price, setPrice] = useState(60000);
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // 'hodl' | 'sold'
  const goal = 20;

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      setElapsed((e) => {
        const ne = e + 1;
        // Price drops 0–8% randomly, mostly down for 12 sec, then recovers
        setPrice((p) => {
          if (ne < 12) return Math.max(15000, p * (0.93 + Math.random() * 0.04));
          return Math.min(75000, p * (1.05 + Math.random() * 0.04));
        });
        if (ne >= goal) {
          setRunning(false);
          setResult("hodl");
        }
        return ne;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (result) {
      const pts = result === "hodl" ? 100 : Math.floor(elapsed * 3);
      setTimeout(() => onComplete && onComplete(pts), 1400);
    }
    // eslint-disable-next-line
  }, [result]);

  const sell = () => {
    if (!running) return;
    setRunning(false);
    setResult("sold");
  };

  const dropPct = ((60000 - price) / 60000) * 100;

  return (
    <div data-testid="kids-game-hodl">
      <div className="mb-3 flex items-center gap-4">
        <Personaje
          id="oracle"
          estado={result === "hodl" ? "feliz" : result === "sold" ? "triste" : running ? "hablar" : "idle"}
          size={80}
        />
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-purple-300">HODL CHALLENGE</div>
          <div className="text-lg font-black text-white">
            ${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div className={`text-xs ${dropPct > 0 ? "text-red-300" : "text-emerald-300"}`}>
            {dropPct > 0 ? "▼" : "▲"} {Math.abs(dropPct).toFixed(1)}%
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/40">{ui.mission}</div>
          <div className="font-mono text-2xl font-black text-emerald-300">{elapsed}/{goal}s</div>
        </div>
      </div>

      <div className="relative h-3 overflow-hidden rounded-full bg-white/[0.06]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-purple-500 to-emerald-400 transition-all duration-700"
          style={{ width: `${(elapsed / goal) * 100}%` }}
        />
      </div>

      <div className="mt-5 flex justify-center gap-3">
        {!running && !result && (
          <button
            onClick={() => { setElapsed(0); setPrice(60000); setRunning(true); }}
            className="rounded-xl bg-gradient-to-r from-purple-500 to-fuchsia-500 px-6 py-3 text-xs font-black uppercase tracking-widest text-white"
          >
            {ui.play}
          </button>
        )}
        {running && (
          <button
            onClick={sell}
            data-testid="kids-hodl-sell"
            className="rounded-xl border-2 border-red-400/50 bg-red-400/10 px-6 py-3 text-xs font-black uppercase tracking-widest text-red-300 transition-colors hover:bg-red-400/20"
          >
            😱 ¡VENDER!
          </button>
        )}
        {result === "hodl" && (
          <div className="text-center text-lg font-black text-emerald-300">🚀 ¡HODL VICTORIA! +100 {ui.points}</div>
        )}
        {result === "sold" && (
          <div className="text-center text-sm font-bold text-red-300">😢 Vendiste muy pronto. ¡Aguanta más la próxima!</div>
        )}
      </div>
    </div>
  );
};

// ─── Game 4 · ConstruyeBloque ──────────────────────────────────────────
const ConstruyeBloque = ({ lang, onComplete }) => {
  const ui = getKidsUi(lang);
  const TX_NEEDED = 6;
  const [txs, setTxs] = useState([]);
  const [available, setAvailable] = useState(() =>
    Array.from({ length: 10 }, (_, i) => ({ id: i, from: `A${i}`, to: `B${i}`, amt: (Math.random() * 0.5).toFixed(3) }))
  );
  const [done, setDone] = useState(false);

  const addTx = (tx) => {
    if (txs.length >= TX_NEEDED || done) return;
    setTxs((t) => [...t, tx]);
    setAvailable((a) => a.filter((x) => x.id !== tx.id));
  };

  useEffect(() => {
    if (txs.length === TX_NEEDED && !done) {
      setDone(true);
      setTimeout(() => onComplete && onComplete(80), 1500);
    }
    // eslint-disable-next-line
  }, [txs.length]);

  return (
    <div data-testid="kids-game-bloque">
      <div className="mb-3 flex items-center gap-3">
        <Personaje id="miner" estado={done ? "feliz" : "idle"} size={70} />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">¡Construye el bloque!</div>
          <div className="text-sm text-white/70">Añade {TX_NEEDED} transacciones al bloque para minarlo</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Available transactions */}
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-white/40">Transacciones</div>
          <div className="space-y-1.5">
            {available.slice(0, 8).map((tx) => (
              <button
                key={tx.id}
                onClick={() => addTx(tx)}
                className="flex w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-mono text-white/80 transition-colors hover:border-cyan-400/40 hover:bg-cyan-400/10"
              >
                <span>{tx.from} → {tx.to}</span>
                <span className="font-bold text-orange-300">{tx.amt} ₿</span>
              </button>
            ))}
          </div>
        </div>

        {/* Block */}
        <div
          className={`rounded-xl border-2 border-dashed p-3 transition-colors ${
            done ? "border-emerald-400/50 bg-emerald-400/10" : "border-cyan-400/40 bg-cyan-400/5"
          }`}
        >
          <div className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest">
            <span className={done ? "text-emerald-300" : "text-cyan-300"}>BLOQUE</span>
            <span className="text-white/50">{txs.length} / {TX_NEEDED}</span>
          </div>
          <div className="min-h-[200px] space-y-1.5">
            {txs.map((tx, i) => (
              <div
                key={tx.id}
                className="flex items-center justify-between rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 text-xs font-mono text-cyan-200"
                style={{ animation: "kidsTxIn .4s ease forwards" }}
              >
                <span>#{i + 1} · {tx.from} → {tx.to}</span>
                <span className="font-bold">{tx.amt} ₿</span>
              </div>
            ))}
            {done && (
              <div className="mt-3 text-center text-lg font-black text-emerald-300">⛏ ¡Bloque minado! +80 {ui.points}</div>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes kidsTxIn{from{transform:translateX(-10px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
};

// ─── Game 5 · MaquinaHalving ───────────────────────────────────────────
const MaquinaHalving = ({ lang, onComplete }) => {
  const ui = getKidsUi(lang);
  const HALVINGS = [
    { year: 2012, reward: 50, after: 25 },
    { year: 2016, reward: 25, after: 12.5 },
    { year: 2020, reward: 12.5, after: 6.25 },
    { year: 2024, reward: 6.25, after: 3.125 },
  ];
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);
  const [pulling, setPulling] = useState(false);
  const h = HALVINGS[idx];

  const trigger = () => {
    if (pulling) return;
    setPulling(true);
    setTimeout(() => {
      setPulling(false);
      if (idx + 1 < HALVINGS.length) {
        setIdx((i) => i + 1);
      } else {
        setDone(true);
        setTimeout(() => onComplete && onComplete(75), 1200);
      }
    }, 900);
  };

  return (
    <div data-testid="kids-game-halving">
      <div className="mb-4 flex items-center gap-3">
        <Personaje id="miner" estado={done ? "feliz" : "idle"} size={80} />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">LA MÁQUINA DEL HALVING</div>
          <div className="text-sm text-white/70">Tira de la palanca para activar cada halving histórico</div>
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/[0.06] to-purple-500/[0.06] p-6 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">Halving · {h.year}</div>
        <div className="my-3 flex items-center justify-center gap-3 text-3xl font-black text-orange-300">
          <span className={pulling ? "animate-pulse" : ""}>{pulling ? h.after : h.reward}</span>
          <span className="text-white/40">→</span>
          <span className={pulling ? "animate-bounce text-emerald-300" : "text-white/40"}>{h.after}</span>
        </div>
        <div className="mb-4 text-xs text-white/60">BTC por bloque</div>

        {!done ? (
          <button
            onClick={trigger}
            disabled={pulling}
            data-testid="kids-halving-lever"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 px-6 py-3 text-xs font-black uppercase tracking-widest text-white shadow-[0_0_25px_rgba(0,212,255,.35)] transition-transform hover:-translate-y-0.5 disabled:opacity-50"
          >
            {pulling ? "⚡ DIVIDIENDO…" : `🔧 Tirar Palanca · Halving ${idx + 1}/${HALVINGS.length}`}
          </button>
        ) : (
          <div className="text-lg font-black text-emerald-300">
            🎉 ¡4 halvings completados! +75 {ui.points}
          </div>
        )}
        <div className="mt-4 flex justify-center gap-1.5">
          {HALVINGS.map((_, i) => (
            <span
              key={i}
              className="h-1.5 w-8 rounded-full"
              style={{ background: i <= idx ? "#00d4ff" : "rgba(255,255,255,0.1)" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Public dispatcher
export const KidsGame = ({ id, lang = "es", onComplete }) => {
  if (id === "atrapa-monedas") return <AtrapaMonedas lang={lang} onComplete={onComplete} />;
  if (id === "quiz-satoshi") return <QuizSatoshi lang={lang} onComplete={onComplete} />;
  if (id === "hodl-challenge") return <HodlChallenge lang={lang} onComplete={onComplete} />;
  if (id === "construye-bloque") return <ConstruyeBloque lang={lang} onComplete={onComplete} />;
  if (id === "maquina-halving") return <MaquinaHalving lang={lang} onComplete={onComplete} />;
  return null;
};

export default KidsGame;
